import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { GamesAudio, GamesService } from '@core/services/games';
import { environment } from '@env';
import { Operator } from '@games/core/operator';
import { TranslateModule } from '@ngx-translate/core';
import { FitTextModule } from '@shared/components/games';
import { Subscription } from 'rxjs';
import { GameAudio } from '@games/core/controller/component-blocks/audio';
import { GameConfigsService } from '@games/shared/game-configs.service';

const DEFAULT_CONFIG = {
  labels: ['gameSettings.ready', 'gameSettings.steady', 'gameSettings.go'],
  idleLabel: 'gameSettings.idle',
  periodDuration: environment.production ? 800 : 100,
  releaseDuration: environment.production ? 1000 : 100,
};

type CountdownState = 'stopped' | 'running';

@Component({
  selector: 'ms-game-countdown',
  imports: [CommonModule, TranslateModule, FitTextModule],
  templateUrl: './game-countdown.component.html',
  styleUrls: ['./game-countdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GameCountdownComponent {
  @Input() operator: Operator;

  readonly private;
  _gameConfigsService = inject(GameConfigsService);

  readonly gameAudio = inject(GameAudio, { optional: true });

  /** Если `true`, при создании компонента запуск не произойдет, и будет ожидаться вызов метода {@link GameCountdownComponent.run}. */
  @Input() manualStart: boolean = false;

  @Input() set config({
    labels,
    idleLabel,
    periodDuration,
    totalDuration,
    releaseDuration,
    isSound,
  }: Partial<{
    labels: string[];
    idleLabel: string;
    periodDuration: number;
    totalDuration: number;
    releaseDuration: number;
    isSound: boolean;
  }>) {
    this._labels = labels ?? this._labels;
    this._idleLabel = idleLabel ?? this._idleLabel;
    this._periodDuration = periodDuration ?? this._periodDuration;
    this._totalDuration = totalDuration ?? this._totalDuration;
    this._releaseDuration = releaseDuration ?? this._releaseDuration;
    this._isSound = isSound ?? this._isSound;
  }

  @Output() stateChange = new EventEmitter<CountdownState>();

  @Output('end') endEventEmitter = new EventEmitter<void>();

  public get currentLabel(): string {
    return this._currentLabel;
  }

  public get isRunning(): boolean {
    return this._isRunning;
  }

  private _currentLabel: string;

  private _isRunning: boolean = false;

  private _labelIndex: number = 0;

  private _periodDuration: number = DEFAULT_CONFIG.periodDuration;

  private _totalDuration: number | null = null;

  private _releaseDuration: number = DEFAULT_CONFIG.releaseDuration;

  private _labels: string[] = DEFAULT_CONFIG.labels;

  private _idleLabel: string = DEFAULT_CONFIG.idleLabel;

  private _isSound: boolean = true;

  private _timeoutSubscription?: Subscription;

  constructor(
    private readonly _gamesService: GamesService,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (this.manualStart) {
      this._showIdleLabel();
    } else {
      this.run();
    }
  }

  ngOnDestroy() {
    this.stop();
  }

  run() {
    const periodDuration = this._totalDuration
      ? this._totalDuration / this._labels.length
      : this._gameConfigsService.periodDuration();

    this._isRunning = true;
    this.stateChange.next('running');

    this._labelIndex = 0;
    this._showLabel();

    this._timeoutSubscription?.unsubscribe();
    this._timeoutSubscription = this._gamesService
      .setTimeout(periodDuration, this._labels.length, true)
      .subscribe({
        next: () => {
          this._labelIndex++;
          this._showLabel();
        },
        error: () => {},
        complete: () => {
          this._timeoutSubscription = this._gamesService
            .setTimeout(this._gameConfigsService.releaseDuration(), 1)
            .subscribe(() => {
              this.stop();
              this.endEventEmitter.next(void 0);
            });
        },
      });
  }

  stop() {
    this._timeoutSubscription?.unsubscribe();

    this._showIdleLabel();
    this._isRunning = false;
    this.stateChange.next('stopped');
  }

  private _showIdleLabel() {
    this._currentLabel = this._idleLabel;
    this._cdr.markForCheck();
  }

  private _showLabel() {
    this._currentLabel = this._labels[this._labelIndex];
    this._cdr.markForCheck();
    if (this._isSound && this.gameAudio) {
      this.gameAudio.alias('countdown');
    }

    if (this._isSound && !this.gameAudio && !environment.production) {
      console.error('Для GameCountdownComponent разрешен звук, но не предоставлен GamesAudio.');
    }
  }
}
